/**
 * Library id derivation — the identity an imported template is upserted under.
 *
 * The backend's import endpoint upserts by `library_id`, so this value decides
 * whether re-importing a corrected file updates the existing template or
 * creates a second copy of it. Derivation must therefore be deterministic:
 * the same file imported twice has to produce the same id both times.
 */

/**
 * Lowercase, strip diacritics, collapse everything else to single hyphens.
 *
 * Diacritics are stripped rather than dropped so "Café Menu" and "Cafe Menu"
 * land on the same slug — for identity purposes they are the same template
 * name typed by two different people.
 *
 * @param {string} name
 * @returns {string} may be empty, when the name held nothing sluggable
 */
export const slugify = (name) =>
  String(name)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * The template's own `library_id`, or one derived from its name.
 *
 * @param {{library_id?: string, name?: string}} doc
 * @returns {string}
 */
export const deriveLibraryId = (doc) => {
  const explicit = doc && typeof doc.library_id === 'string' ? doc.library_id.trim() : '';
  if (explicit) return explicit;

  const slug = slugify(doc && doc.name ? doc.name : '');
  return slug ? `imported-${slug}` : 'imported-untitled';
};
